import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'

import { getToolDisplayName } from '@/lib/assistant-tool-metadata'
import type { ToolCallState } from '@/contexts/chat-context'

// transitions.dev "thinking states": the line shimmers while a state
// holds, then swaps — the outgoing line exits up through a blur while
// the incoming one rises in from below after a short gap.
const THINK_EXIT_MS = 150
const THINK_GAP_MS = 50

const shimmerSx = {
	position: 'absolute',
	inset: 0,
	color: 'transparent',
	WebkitTextFillColor: 'transparent',
	backgroundImage:
		'linear-gradient(90deg, transparent 0%, transparent 40%, var(--mui-palette-text-primary) 50%, transparent 60%, transparent 100%)',
	backgroundSize: '400% 100%',
	backgroundRepeat: 'no-repeat',
	WebkitBackgroundClip: 'text',
	backgroundClip: 'text',
	animation: 'thinkShimmer 2000ms linear infinite',
	'@keyframes thinkShimmer': {
		'0%': { backgroundPosition: '100% 0' },
		'100%': { backgroundPosition: '0% 0' },
	},
	'@media (prefers-reduced-motion: reduce)': { display: 'none' },
} as const

function getThinkingLabel(toolCalls?: ToolCallState[]): string {
	const running = toolCalls?.find((tc) => tc.status === 'running')
	if (running) return `Checking ${getToolDisplayName(running.name).toLowerCase()}…`
	if (toolCalls?.length) return 'Writing response…'
	return 'Thinking…'
}

export function ThinkingLine({ delay = 0, exiting, text }: { delay?: number; exiting?: boolean; text: string }) {
	return (
		<Box
			aria-hidden={exiting || undefined}
			component="span"
			sx={{
				position: exiting ? 'absolute' : 'relative',
				top: exiting ? 0 : undefined,
				left: exiting ? 0 : undefined,
				display: 'inline-block',
				whiteSpace: 'nowrap',
				color: 'text.secondary',
				animation: exiting
					? `thinkExit ${THINK_EXIT_MS}ms ease-in-out both`
					: `thinkEnter ${THINK_EXIT_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${THINK_GAP_MS + delay}ms both`,
				'@keyframes thinkExit': {
					from: { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
					to: { opacity: 0, transform: 'translateY(-8px)', filter: 'blur(2px)' },
				},
				'@keyframes thinkEnter': {
					from: { opacity: 0, transform: 'translateY(8px)', filter: 'blur(2px)' },
					to: { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
				},
				'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
			}}
		>
			{text}
			<Box aria-hidden component="span" sx={shimmerSx}>
				{text}
			</Box>
		</Box>
	)
}

export function ThinkingIndicator({ label: fixedLabel, toolCalls }: { label?: string; toolCalls?: ToolCallState[] }) {
	const label = fixedLabel ?? getThinkingLabel(toolCalls)
	const [current, setCurrent] = useState(label)
	const [exiting, setExiting] = useState<string | null>(null)

	useEffect(() => {
		if (label === current) return
		setExiting(current)
		setCurrent(label)
		const timer = window.setTimeout(() => setExiting(null), THINK_EXIT_MS + THINK_GAP_MS)
		return () => window.clearTimeout(timer)
	}, [label, current])

	return (
		<Box
			role="status"
			sx={{
				position: 'relative',
				display: 'flex',
				alignItems: 'center',
				height: '1.75em',
				fontSize: '0.8125rem',
				letterSpacing: '-0.02em',
			}}
		>
			<ThinkingLine key={current} text={current} />
			{exiting ? <ThinkingLine exiting text={exiting} /> : null}
		</Box>
	)
}
